import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import { useTranslation } from 'react-i18next';
import NotificationActorAvatar from '../UserAvatar/NotificationActorAvatar';
import CredibilityChip from '../Credibility/CredibilityChip';
import { Dropdown } from '../ui';
import {
  Bell,
  CheckCheck,
  Clock,
  AlertCircle,
  Info,
  X,
  CheckCircle2,
  ClipboardList
} from 'lucide-react';
import {
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadNotificationCount
} from '../../redux/notifications/notificationsActionThunks';
import { logAnalyticsEvent } from '../../redux/analytics/analyticsEventsAPI';
import { fireConfettiFromElement } from '../../utils/celebration';

// Create memoized selectors OUTSIDE the component
const selectNotificationsState = (state) => state.notifications.notifications;
const selectCurrentUser = (state) => state.profile.user;
const selectLoadingState = (state) => state.notifications.loading;
const selectErrorState = (state) => state.notifications.error;
const selectUnreadCounts = (state) => state.notifications.unreadCounts;

// Create a memoized selector for user-specific notifications
const selectUserNotifications = createSelector(
  [selectNotificationsState, selectCurrentUser],
  (notifications, currentUser) => {
    if (!currentUser?.id) return [];
    return notifications[currentUser.id] || [];
  }
);

const NotificationsDropdown = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get current user and notifications using the memoized selectors
  const currentUser = useSelector(selectCurrentUser);
  const notifications = useSelector(selectUserNotifications);
  const loading = useSelector(selectLoadingState);
  const error = useSelector(selectErrorState);
  const unreadCounts = useSelector(selectUnreadCounts);

  // The notification LIST is capped server-side (limit=7 by default), so a
  // user with more unread than that would undercount if we derived the
  // badge from list length alone. GET /unread-count has no such cap --
  // prefer it once it has loaded, and only fall back to the list-derived
  // count before that first fetch resolves.
  const realUnreadCount = currentUser?.id ? unreadCounts[currentUser.id] : undefined;
  const listDerivedUnreadCount = notifications.filter(notification => !notification.is_read).length;
  const unreadCount = realUnreadCount !== undefined ? realUnreadCount : listDerivedUnreadCount;
  
  // Track fetch state
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Setup polling for new notifications
  useEffect(() => {
    if (currentUser?.id) {
      // Initial fetch
      dispatch(fetchUserNotifications(currentUser.id))
        .finally(() => setNotificationsLoaded(true));
      dispatch(getUnreadNotificationCount(currentUser.id));

      // Setup polling interval (every 2 minutes)
      const interval = setInterval(() => {
        dispatch(fetchUserNotifications(currentUser.id));
        dispatch(getUnreadNotificationCount(currentUser.id));
        setLastFetchTime(Date.now());
      }, 120000); // 2 minutes

      setPollingInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [currentUser, dispatch]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (currentUser?.id && isOpen) {
      dispatch(fetchUserNotifications(currentUser.id));
      dispatch(getUnreadNotificationCount(currentUser.id));
      setLastFetchTime(Date.now());
    }
  }, [currentUser, isOpen, dispatch]);

  // Handle notification click - navigate to notifications page, except
  // Company Ops task notifications which deep-link straight to the task
  // (see adminDashboard.jsx's ?tab=/taskId= query-param handling).
  const handleNotificationClick = useCallback((notification) => {
    logAnalyticsEvent('notification_opened', { notification_type: notification?.type });
    if (notification?.type === 'task_assigned' || notification?.type === 'task_completed') {
      navigate(`/admin?tab=companyOps&sub=tasks&taskId=${notification.related_id}`);
    } else {
      // Always navigate to notifications page for every other type.
      window.location.href = "/notifications";
    }
    setIsOpen(false);
  }, [navigate]);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback((e) => {
    e.stopPropagation(); // Prevent dropdown from closing
    if (currentUser?.id && unreadCount > 0) {
      dispatch(markAllNotificationsAsRead(currentUser.id));
      fireConfettiFromElement(e.currentTarget);
    }
  }, [currentUser, dispatch, unreadCount]);

  // Helper function to get icon based on notification type
  const getNotificationIcon = useCallback((type) => {
    switch (type) {
      case 'alert':
        return <AlertCircle className="w-5 h-5 text-purple-500" />;
      case 'success':
        return <CheckCheck className="w-5 h-5 text-green-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'task_assigned':
      case 'task_completed':
        return <ClipboardList className="w-5 h-5 text-indigo-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  }, []);

  // Format relative time
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    
    try {
      const now = new Date();
      const notificationTime = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(notificationTime.getTime())) {
        return '';
      }
      
      const diffInSeconds = Math.floor((now - notificationTime) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      
      // For older notifications, show actual date
      return notificationTime.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric',
        year: notificationTime.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (e) {
      console.warn("Error formatting notification time:", e);
      return '';
    }
  }, []);

  // Only show the unread indicator if:
  // 1. Notifications have loaded
  // 2. There are actually unread notifications
  // 3. User is logged in
  const shouldShowUnreadBadge = notificationsLoaded && unreadCount > 0 && currentUser?.id;

  // Add fade-in animation and improve keyboard accessibility
  return (
    <>
      <Dropdown
        align="right"
        open={isOpen}
        onOpenChange={setIsOpen}
        panelRole="dialog"
        panelAriaLabel={t('nav.notificationsPanelLabel')}
        panelClassName="w-[min(24rem,calc(100vw-2rem))] text-black dark:text-white animate-fade-in"
        trigger={({ toggle }) => (
          <button
            onClick={toggle}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full relative transition-colors"
            aria-label={`${t('nav.notifications')} ${shouldShowUnreadBadge ? `(${unreadCount} unread)` : ''}`}
            aria-expanded={isOpen}
            aria-haspopup="true"
          >
            <Bell className="w-6 h-6" />
            {shouldShowUnreadBadge && (
              <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        )}
      >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg" id="notifications-title">{t('nav.notifications')}</h3>
              <div className="flex items-center space-x-3">
                {unreadCount > 0 && (
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    onClick={handleMarkAllAsRead}
                    aria-label={t('nav.markAllAsRead')}
                  >
                    {t('nav.markAllAsRead')}
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label={t('nav.closeNotifications')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Notification Content Area */}
          <div
            className="max-h-96 overflow-y-auto"
            aria-labelledby="notifications-title"
            role="region"
          >
            {/* Loading State */}
            {loading && !notificationsLoaded && (
              <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                <div className="flex justify-center items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span>{t('nav.loadingNotifications')}</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="p-4">
                <div className="flex items-start p-4 space-x-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg border border-red-200 dark:border-red-800">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium">{t('nav.error')}</h4>
                    <p className="text-sm">{error}</p>
                    <button
                      className="mt-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      onClick={() => dispatch(fetchUserNotifications(currentUser?.id))}
                    >
                      {t('nav.tryAgain')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Not Logged In State */}
            {!currentUser && (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-gray-500 dark:text-gray-400">
                <Bell className="w-12 h-12 mb-2 text-gray-300 dark:text-gray-600" />
                <p>{t('nav.pleaseLogIn')}</p>
              </div>
            )}

            {/* Empty State -- "all caught up", not just "nothing found", so it
                gets a celebratory checkmark rather than the plain bell. */}
            {!loading && !error && currentUser && notifications.length === 0 && notificationsLoaded && (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-gray-500 dark:text-gray-400">
                <div className="p-2.5 bg-emerald-100 dark:bg-emerald-500/10 rounded-full mb-2 animate-pop">
                  <CheckCircle2 className="w-9 h-9 text-emerald-700 dark:text-emerald-400" />
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">{t('nav.allCaughtUp')}</p>
                <p className="text-sm mt-1">{t('nav.allCaughtUpSubtext')}</p>
              </div>
            )}

            {/* Notifications List */}
            {!loading && !error && notifications.length > 0 && (
              <>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    data-notification-id={notification.id}
                    className="notification-item px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-800 last:border-b-0 cursor-pointer transition-colors"
                    onClick={() => handleNotificationClick(notification)}
                    role="button"
                    tabIndex="0"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                    aria-label={`${notification.title} - Click to view all notifications`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {notification.actor_username ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsOpen(false);
                              navigate(`/public-profiles/${encodeURIComponent(notification.actor_username)}`);
                            }}
                            title={`View ${notification.actor_username}'s profile`}
                            className="block rounded-full ring-2 ring-transparent hover:ring-blue-500/50 transition-all"
                          >
                            <NotificationActorAvatar
                              avatarUrl={notification.actor_avatar_url}
                              username={notification.actor_username}
                              size="w-8 h-8"
                              textSize="text-xs"
                            />
                          </button>
                        ) : (
                          getNotificationIcon(notification.type)
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {notification.title}
                            </p>
                            {notification.actor_username && (
                              <CredibilityChip cred={notification.actor_credibility} size="xs" showAccuracy={false} showTier={false} />
                            )}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                            {formatTime(notification.created_at || notification.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {notification.content || notification.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
              <button 
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white w-full text-center transition-colors"
                onClick={() => {
                  window.location.href = "/notifications";
                  setIsOpen(false);
                }}
              >
                {t('nav.viewAllNotifications')}
              </button>
            </div>
          )}
      </Dropdown>

      {/* Add global styles for animations - Remove jsx attribute */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default NotificationsDropdown;